import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { AuthenticatedUser, LoginUser } from '@app/shared/models/authenticated-user.model';
import { EncryptingService } from '../services/encrypting.service';
import { LoginService } from './login.service';
import { Router } from '@angular/router';
import { SelectedAccountStore } from '../stores/selected-account-store.service';
import { BehaviorSubject, lastValueFrom, Observable } from 'rxjs';
import { ENDPOINTS } from '../constants/endpoints';
import { UserPreferencesStore } from '../stores/user-preferences-store.service';
import {
    AUTHENTICATION_TOKEN_KEY,
    AUTHENTICATION_TOKEN_REFRESH,
    COMPANY_URI,
    COMPANY_URLS,
    DASHBOARDS_KEY,
    METRICS_KEY,
} from '../constants/storage-keys';
import { UserService } from '../services/users/user.service';
import moment from 'moment';

@Injectable()
export class AuthenticationService {
    private _token: string;
    private userSubject: BehaviorSubject<AuthenticatedUser>;

    get token(): string {
        return this._token;
    }

    constructor(
        private loginService: LoginService,
        private cookieService: CookieService,
        private encryptingService: EncryptingService,
        private router: Router,
        private selectedAccountStore: SelectedAccountStore,
        private userPreferencesStore: UserPreferencesStore,
        private usersService: UserService
    ) {
        this.userSubject = new BehaviorSubject<AuthenticatedUser>(undefined);
    }

    get user$(): Observable<AuthenticatedUser> {
        return this.userSubject.asObservable();
    }

    set user(value) {
        this.userSubject.next(value);
    }

    get user(): AuthenticatedUser {
        return this.userSubject.value;
    }

    async login(email: string, password: string, redirectToDashboard = true): Promise<void> {
        const user = await lastValueFrom(this.loginService.login(email, password));

        if (user && user.token) {
            this._token = user.token;

            this.userSubject.next({
                id: user.id,
                name: user.name,
                domain: user.domain,
                serviceAccount: user.serviceAccount,
                childAccounts: user.childAccounts,
            });

            this.storeTokenData(user);
            await this.setCookies(user);

            this.selectedAccountStore.initialize(user);
            await this.userPreferencesStore.initialize(user.id);

            if (redirectToDashboard) {
                this.router.navigate([ENDPOINTS.urls.dashboard.base]).catch(() => {});
            }
        } else {
            this.logout(false).catch(() => {});
        }
    }

    async refreshToken(): Promise<LoginUser> {
        let logout = false;

        try {
            const user = await lastValueFrom(this.loginService.refreshToken(this.token));

            if (user && user.token) {
                this._token = user.token;

                this.userSubject.next({
                    id: user.id,
                    name: user.name,
                    domain: user.domain,
                    serviceAccount: user.serviceAccount,
                    childAccounts: user.childAccounts,
                });
                this.selectedAccountStore.update(user);
                this.storeTokenData(user);
                await this.setCookies(user);
                return user;
            } else {
                logout = true;
            }
        } catch {
            logout = true;
        }

        if (logout) {
            this.logout(true).catch(() => {});
            return Promise.reject();
        }
    }

    async logout(redirectToLogin: boolean): Promise<void> {
        this._token = undefined;
        this.deleteCookies();
        localStorage.clear();
        this.userSubject.next(undefined);
        this.userPreferencesStore.clear();

        if (redirectToLogin) {
            await this.router.navigate([ENDPOINTS.urls.login]);
        }

        this.selectedAccountStore.reset();
    }

    async loadAuthenticatedUserFromToken(): Promise<void> {
        const storedData = localStorage.getItem(AUTHENTICATION_TOKEN_KEY)?.toString();

        if (storedData) {
            const userRawData = this.encryptingService.decrypt(storedData) as string;
            this._token = userRawData['token'];
            const user = await this.refreshToken();
            this.selectedAccountStore.initialize(user);
        }
    }

    isAuthenticated(): boolean {
        return !!this._token && !!this.user;
    }

    private deleteCookies() {
        this.cookieService.delete(DASHBOARDS_KEY);
        this.cookieService.delete(METRICS_KEY);
        this.cookieService.delete(COMPANY_URI);
        this.cookieService.delete(COMPANY_URLS);
    }

    private storeTokenData(user: LoginUser) {
        localStorage.setItem(
            AUTHENTICATION_TOKEN_KEY,
            this.encryptingService.encrypt(user.rawValue)
        );

        localStorage.setItem(
            AUTHENTICATION_TOKEN_REFRESH,
            moment().add(1, 'hour').valueOf().toString()
        );
    }

    private async setCookies(user: LoginUser) {
        this.cookieService.set(
            DASHBOARDS_KEY,
            JSON.stringify(
                user.serviceAccount
                    ? user.serviceAccount.dashboards
                    : user.childAccounts[0].dashboards
            ),
            365,
            '/'
        );

        await this.setCompaniesCookie(user);
    }

    private async setCompaniesCookie(user: AuthenticatedUser) {
        const companies = await lastValueFrom(this.usersService.getCompanies(user));
        this.cookieService.set(COMPANY_URLS, JSON.stringify(companies.items), 365, '/');
    }
}
